import { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Crosshair } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { cn } from '@/lib/cn';
import { cartoApiKey } from '@/lib/env';

// Leaflet's default icon URLs break under a bundler; point them at the packaged assets.
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

/**
 * mergeOptions alone is not enough, and this is why the pins rendered broken.
 *
 * `Icon.Default` overrides `_getIconUrl` to PREPEND an auto-detected `imagePath` to
 * whatever `iconUrl` holds (leaflet-src.js:7507). Handed a bundler-resolved URL that
 * is already absolute, it produced `<detectedPath>/node_modules/...png` and 404'd.
 * Deleting the override falls back to `Icon.prototype._getIconUrl`, which returns
 * the option verbatim.
 */
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

/**
 * The crash pin. `tone: 'critical'` was in MapMarker's type and passed by
 * CrashDetailPage, but nothing ever read it — so the site of a critical crash
 * rendered identically to any other point on the map.
 *
 * Colour is not doing the work on its own (FRONTEND.md): this is a different shape
 * and larger than the default pin, so it survives colour-blindness and a greyscale
 * print of a crash record.
 */
const CRITICAL_ICON = L.divIcon({
  className: '',
  html: `<svg width="34" height="44" viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Crash location">
  <path d="M17 43C17 43 32 26.5 32 16.5A15 15 0 1 0 2 16.5C2 26.5 17 43 17 43Z"
        fill="#b91c1c" stroke="#ffffff" stroke-width="2.5" stroke-linejoin="round"/>
  <path d="M17 8.5v10" stroke="#ffffff" stroke-width="3" stroke-linecap="round"/>
  <circle cx="17" cy="24.5" r="1.9" fill="#ffffff"/>
</svg>`,
  iconSize: [34, 44],
  // Anchored at the point of the pin, not its centre, so it marks the actual spot.
  iconAnchor: [17, 43],
  popupAnchor: [0, -38],
});

export interface MapMarker {
  id: string | number;
  position: [number, number];
  title: string;
  subtitle?: string;
  tone?: 'default' | 'critical';
}

/**
 * CARTO Positron — a muted light basemap, chosen so the map does not fight the UI
 * around it (FRONTEND.md). Deliberately NOT the Voyager rastertiles endpoint: that
 * is full-colour and would undo that choice.
 *
 * The key removes CARTO's "API key required" watermark. Unkeyed tiles still load,
 * so this degrades to a watermark rather than an empty map.
 */
const TILE_URL =
  'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png' +
  (cartoApiKey ? `?key=${cartoApiKey}` : '');

/** Bengaluru — only used when there is genuinely nothing to show. */
const FALLBACK_CENTER: [number, number] = [12.9716, 77.5946];

/**
 * Keeps the view on the tracked point, but stops the instant the user touches the
 * map. The previous implementation called setView on every poll, so any attempt to
 * pan or zoom was yanked back within five seconds and the map was unusable.
 */
function FollowController({
  center,
  following,
  onUserInteract,
}: {
  center: [number, number] | null;
  following: boolean;
  onUserInteract: () => void;
}) {
  const map = useMap();
  // setView fires movestart/zoomstart too, so without this flag the map would
  // treat its own recentering as the user taking over and stop following at once.
  const programmatic = useRef(false);

  useMapEvents({
    dragstart: onUserInteract,
    zoomstart: () => {
      if (!programmatic.current) onUserInteract();
    },
  });

  useEffect(() => {
    if (!following || !center) return;

    programmatic.current = true;
    map.setView(center, map.getZoom(), { animate: true });

    // Cleared after the event loop turn in which Leaflet emits its move events.
    const release = setTimeout(() => {
      programmatic.current = false;
    }, 0);

    return () => clearTimeout(release);
  }, [map, following, center]);

  return null;
}

/** Frames all markers once, on first data, and then leaves the view alone. */
function InitialFit({ markers }: { markers: MapMarker[] }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (fitted.current || markers.length === 0) return;
    fitted.current = true;

    if (markers.length === 1 && markers[0]) {
      map.setView(markers[0].position, 15);
      return;
    }

    map.fitBounds(L.latLngBounds(markers.map((marker) => marker.position)), { padding: [40, 40] });
  }, [map, markers]);

  return null;
}

interface BaseMapProps {
  markers: MapMarker[];
  /** When set, the map follows this point until the user takes over. */
  followPoint?: [number, number] | null;
  className?: string;
  height?: string;
}

export function BaseMap({ markers, followPoint = null, className, height = 'h-80' }: BaseMapProps) {
  const [following, setFollowing] = useState(true);

  const initialCenter = followPoint ?? markers[0]?.position ?? FALLBACK_CENTER;
  const canFollow = followPoint !== null;

  return (
    <div className={cn('relative isolate overflow-hidden rounded-[--radius-card]', height, className)}>
      <MapContainer
        center={initialCenter}
        zoom={13}
        scrollWheelZoom
        className="size-full"
        attributionControl
      >
        <TileLayer
          url={TILE_URL}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          subdomains={['a', 'b', 'c', 'd']}
          maxZoom={19}
        />

        <InitialFit markers={markers} />
        {canFollow && (
          <FollowController
            center={followPoint}
            following={following}
            onUserInteract={() => setFollowing(false)}
          />
        )}

        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            {...(marker.tone === 'critical' ? { icon: CRITICAL_ICON } : {})}
          >
            <Popup>
              <span className="font-medium">{marker.title}</span>
              {marker.subtitle && (
                <>
                  <br />
                  <span className="text-ink-secondary">{marker.subtitle}</span>
                </>
              )}
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {canFollow && !following && (
        <button
          onClick={() => setFollowing(true)}
          className="absolute bottom-3 left-1/2 z-[1000] -translate-x-1/2 rounded-full border border-line-strong bg-surface px-3 py-1.5 text-[13px] font-medium text-ink shadow-sm transition-colors hover:bg-surface-sunken"
        >
          <span className="inline-flex items-center gap-1.5">
            <Crosshair className="size-3.5" aria-hidden />
            Follow vehicle
          </span>
        </button>
      )}
    </div>
  );
}
